<!--
File: TreatmentMatrixUnpavedList.vue
Description: shows the Treatment Matrix for Unpaved Roads as the list.
-->
<template>
  <base-data-table
    v-if="eligible"
    :loading="showSpinner"
    :pagination="pagination"
    :from="from"
    :to="to"
    :total="total"
    :btnAddAllowed="isBtnAllowed('AddButton')"
    @add-item="viewItem()">

    <template #header-filters>
      <div :class="getSize(15)">
        <BaseDropdown v-model="selectedTreatment" :label="$t('selections.select_treatment')" :items="treatmentKeys" />
      </div>
    </template>

    <template #table-rows="{ item }">
      <base-table
        :items="queriedData"
        :headers="headers"
        :idFieldName="idFieldName"
        :sort="currentSort"
        :sortOrder="currentSortOrder">

        <template #table-actions="{ item }">
          <table-actions
            :btnEditAllowed="isBtnAllowed('EditButton')"
            :btnDeleteAllowed="isBtnAllowed('DeleteButton')"
            @edit="viewItem(item[idFieldName])"
            @delete="deleteItem(item[idFieldName], item.description)" />
        </template>

      </base-table>
    </template>

    <template #editing-dialog>
      <EditForm
        v-if="showDetailsDlg"
        :itemId="currentId"
        :actionBase="'treatment_matrix_unpaved'"
        :moduleName="'ReferenceData'"
        :formName="formName"
        :idFieldName="idFieldName"
        :fields="fields"
        :validations="validations"
        :messageTitle="$t('route.treatment_matrix_unpaved')"
        @close="showDetailsDlg = false"
      />
    </template>
  </base-data-table>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import { BaseTable, BaseDataTable, TableActions, BaseDropdown, EditForm } from '@/pages/Components'
import BaseTableMixin from '@/mixins/BaseTableMixin'
import { deleteConfirmation, deletedMessage, errorMessage } from '@/mixins/messagesMixin'
import Fuse from 'fuse.js'

export default {
  name: 'TreatmentMatrixUnpavedList',
  mixins: [BaseTableMixin],

  data() {
    return {
      formName: 'TreatmentMatrixList', // Use paved list permissions
      idFieldName: 'treatment_matrix_unpaved_id',
      propsToSearch: ['key', 'description', 'aadt', 'iri', 'expected_outcome'],

      selectedTreatment: null,
      currentSort: 'description',

      historyMapping: {
        selectedTreatment: null,
        currentSort: 'description',
        currentSortOrder: 'asc',
        pagination: this.pagination,
        searchQuery: ''
      }
    }
  },

  components: {
    BaseTable,
    BaseDataTable,
    TableActions,
    BaseDropdown,
    EditForm
  },

  methods: {
    ...mapActions({
      clearList: 'ReferenceData/CLEAR_TREATMENT_MATRIX_UNPAVED_LIST',
      loadList: 'ReferenceData/LOAD_TREATMENT_MATRIX_UNPAVED_LIST',
      delete: 'ReferenceData/DELETE_TREATMENT_MATRIX_UNPAVED',
    }),

    // Override deleteItem to add refresh flag functionality
    async deleteItem(id, descr) {
      // Use the same confirmation logic as the parent mixin
      this.save_history();
      const { value } = await deleteConfirmation(descr);
      if (!value) return;
      
      try {
        await this.delete(id);
        this.reloadData();
        
        // Set refresh flag for matrix tab
        this.onDataModified();
        
        await deletedMessage(descr);
      } catch (err) {
        await errorMessage(err.message || this.$t('errors.unknown_error'));
      }
    },

    // Override reloadData to detect all data changes (add/edit/delete)
    async reloadData() {
      const wasDataLoaded = this.tableData && this.tableData.length > 0;
      
      // Call parent reloadData method
      try {
        this.showSpinner = true
        await this.loadList(this.payload)
        this.fuseSearch = new Fuse(this.tableData, {
          keys: this.propsToSearch,
          threshold: 0.1
        })
        
        // If this is a data reload (not initial load), set refresh flag
        if (wasDataLoaded) {
          this.onDataModified();
        }
      } catch (error) {
        console.error('Error loading items:', error)
      } finally {
        this.showSpinner = false
      }
    },

    // Set flag for pivot refresh when data is modified
    onDataModified() {
      localStorage.setItem('unpaved_matrix_needs_refresh', 'true');
      this.$emit('data-modified');
    },
  },

  computed: {
    ...mapGetters('ReferenceData', ['getTreatmentMatrixUnpavedUniqueValues', 'matrixUnpavedCombined']),

    tableData() {
      return this.matrixUnpavedCombined(this.selectedTreatment);
    },

    headers() {
      return [
        { header: "stdCols.key", key: "key", sortable: true },
        { header: "stdCols.description", key: "description", sortable: true },
        { header: "condition.aadt", key: "aadt", sortable: true },
        { header: "condition.iri", key: "iri", sortable: true },
        { header: "label.expected_outcome", key: "expected_outcome", sortable: true },
      ]
    },

    fields() {
      return [
        { name: 'fk_treatment_type', type: 'custom', component: 'TreatmentsDropdown', class: 'md-layout-item md-size-100' },
        { name: 'iri_from', label: `${this.$t('condition.iri')}, ${this.$t('label.value_from')}`, type: 'number', class: 'md-layout-item md-size-50' },
        { name: 'iri_to', label: `${this.$t('condition.iri')}, ${this.$t('label.value_to')}`, type: 'number', class: 'md-layout-item md-size-50' },
        { name: 'aadt_from', label: `${this.$t('condition.aadt')}, ${this.$t('label.value_from')}`, type: 'number', class: 'md-layout-item md-size-50' },
        { name: 'aadt_to', label: `${this.$t('condition.aadt')}, ${this.$t('label.value_to')}`, type: 'number', class: 'md-layout-item md-size-50' },
      ];
    },

    validations() {
      return {
        fk_treatment_type: { required: true, numeric: true, min_value: 1 },
        iri_from: { required: true, numeric: true, min_value: 0 },
        iri_to: { required: true, numeric: true, min_value: 0 },
        aadt_from: { required: true, numeric: true, min_value: 0 },
        aadt_to: { required: true, numeric: true, min_value: 0 },
      };
    },

    treatmentKeys() {
      const originalItems = this.getTreatmentMatrixUnpavedUniqueValues('key', 'fk_treatment_type');
      
      // Add blank "Show All" option as first item
      const showAllOption = {
        id: '', // empty string instead of null to show all items
        description: '', // blank description as requested
        key: ''
      };
      
      return [showAllOption, ...originalItems];
    },

    tableData() {
      // If selectedTreatment is empty/null/undefined, show all data by passing null to the store
      if (!this.selectedTreatment || this.selectedTreatment === '' || this.selectedTreatment === null) {
        return this.matrixUnpavedCombined(null);
      }
      // Otherwise filter by selected treatment
      return this.matrixUnpavedCombined(this.selectedTreatment);
    },
  },
}
</script>
<style lang="scss" scoped>
@import '@/mixins/BaseTable.scss';

/* Ensure dropdown has good spacing and visibility */
::v-deep .md-select {
  .md-menu {
    z-index: 1050 !important;
    max-height: 300px !important;
  }
  
  .md-menu-content {
    max-height: 280px !important;
    overflow-y: auto !important;
  }
}
</style>